"use client";

import { createContext, useContext, useMemo, useState } from "react";

export type BillingRangeType = "current" | "previous" | "custom";

type BillingUsageRangeContextType = {
	range: BillingRangeType;
	setRange: (r: BillingRangeType) => void;
	start: string | null; // YYYY-MM-DD
	end: string | null; // YYYY-MM-DD
	setStart: (d: string | null) => void;
	setEnd: (d: string | null) => void;
	// Query object for API calls
	query: { range: BillingRangeType; start?: string; end?: string };
};

const BillingUsageRangeContext =
	createContext<BillingUsageRangeContextType | null>(null);

export function BillingUsageRangeProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const [range, setRange] = useState<BillingRangeType>("current");
	const [start, setStart] = useState<string | null>(null);
	const [end, setEnd] = useState<string | null>(null);

	const query = useMemo(() => {
		if (range === "custom" && start && end) {
			return { range, start, end };
		}
		return { range } as { range: BillingRangeType };
	}, [range, start, end]);

	const value: BillingUsageRangeContextType = {
		range,
		setRange,
		start,
		end,
		setStart,
		setEnd,
		query,
	};

	return (
		<BillingUsageRangeContext.Provider value={value}>
			{children}
		</BillingUsageRangeContext.Provider>
	);
}

export function useBillingUsageRange() {
	const ctx = useContext(BillingUsageRangeContext);
	if (!ctx)
		throw new Error(
			"useBillingUsageRange must be used within a BillingUsageRangeProvider",
		);
	return ctx;
}
